import type { ResolvedWorkingHours } from '@/types/workingHours'
import { DEFAULT_WORKING_HOURS } from '@/types/workingHours'

// ============================================================================
// Working Hours Resolution
//
// Priority chain:
//   1. User override  – if user_org.working_hours_override is true
//   2. Org settings   – if org.working_hours_enabled is true
//   3. Default        – no schedule (tracking runs all day)
//
// For multi-org users, each org's resolved hours are merged via
// mergeWorkingHours() to produce the widest possible tracking window:
//   - Earliest start across all orgs
//   - Latest end across all orgs
//   - Union of all working days
// ============================================================================

interface UserWorkingHoursFields {
    working_hours_override?: boolean | null
    work_start_time?: string | null
    work_end_time?: string | null
    working_days?: number[] | null
}

interface OrgSettingsWorkingHoursFields {
    working_hours_enabled?: boolean | null
    work_start_time?: string | null
    work_end_time?: string | null
    working_days?: number[] | null
}

export function resolveWorkingHours(
    user: UserWorkingHoursFields | null | undefined,
    orgSettings: OrgSettingsWorkingHoursFields | null | undefined
): ResolvedWorkingHours {
    if (user?.working_hours_override) {
        return {
            enabled: true,
            startTime: user.work_start_time || DEFAULT_WORKING_HOURS.startTime,
            endTime: user.work_end_time || DEFAULT_WORKING_HOURS.endTime,
            workingDays: user.working_days || DEFAULT_WORKING_HOURS.workingDays,
            source: 'user',
        }
    }

    if (orgSettings?.working_hours_enabled) {
        return {
            enabled: true,
            startTime: orgSettings.work_start_time || DEFAULT_WORKING_HOURS.startTime,
            endTime: orgSettings.work_end_time || DEFAULT_WORKING_HOURS.endTime,
            workingDays: orgSettings.working_days || DEFAULT_WORKING_HOURS.workingDays,
            source: 'org',
        }
    }

    return { ...DEFAULT_WORKING_HOURS, source: 'default' }
}

export function formatTime(hours: number, minutes: number): string {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export function parseTime(timeStr: string): { hours: number; minutes: number } {
    const [hours, minutes] = timeStr.split(':').map(Number)
    return { hours, minutes }
}

export function isCurrentlyWithinWorkingHours(hours: ResolvedWorkingHours): boolean {
    if (!hours.enabled) return true

    const now = new Date()
    const currentDay = now.getDay()
    const currentTime = formatTime(now.getHours(), now.getMinutes())

    if (!hours.workingDays.includes(currentDay)) {
        return false
    }

    return currentTime >= hours.startTime && currentTime < hours.endTime
}

// Where is `now` relative to the user's working window?
//   'within'  — inside the window (or no schedule configured)
//   'before'  — earlier than today's start on a working day
//   'after'   — later than today's end on a working day, OR on a non-working day
export type WorkingHoursRelation = 'within' | 'before' | 'after'

export function workingHoursRelation(
    hours: ResolvedWorkingHours,
    now: Date = new Date()
): WorkingHoursRelation {
    if (!hours.enabled) return 'within'
    const currentDay = now.getDay()
    const currentTime = formatTime(now.getHours(), now.getMinutes())
    if (!hours.workingDays.includes(currentDay)) return 'after'
    if (currentTime < hours.startTime) return 'before'
    if (currentTime >= hours.endTime) return 'after'
    return 'within'
}

export function getNextWorkingPeriodStart(hours: ResolvedWorkingHours): Date | null {
    if (!hours.enabled || hours.workingDays.length === 0) return null

    const now = new Date()
    const currentDay = now.getDay()
    const currentTime = formatTime(now.getHours(), now.getMinutes())

    if (hours.workingDays.includes(currentDay) && currentTime < hours.startTime) {
        const { hours: h, minutes: m } = parseTime(hours.startTime)
        const nextStart = new Date(now)
        nextStart.setHours(h, m, 0, 0)
        return nextStart
    }

    for (let i = 1; i <= 7; i++) {
        const checkDay = (currentDay + i) % 7
        if (hours.workingDays.includes(checkDay)) {
            const { hours: h, minutes: m } = parseTime(hours.startTime)
            const nextStart = new Date(now)
            nextStart.setDate(nextStart.getDate() + i)
            nextStart.setHours(h, m, 0, 0)
            return nextStart
        }
    }

    return null
}

export function compareTime(a: string, b: string): number {
    const timeA = parseTime(a)
    const timeB = parseTime(b)
    const minutesA = timeA.hours * 60 + timeA.minutes
    const minutesB = timeB.hours * 60 + timeB.minutes
    return minutesA - minutesB
}

export function isValidTimeRange(startTime: string, endTime: string): boolean {
    return compareTime(startTime, endTime) < 0
}

export function timeToDate(timeStr: string): Date {
    const { hours, minutes } = parseTime(timeStr)
    const date = new Date()
    date.setHours(hours, minutes, 0, 0)
    return date
}

export function dateToTime(date: Date): string {
    return formatTime(date.getHours(), date.getMinutes())
}

export function mergeWorkingHours(schedules: ResolvedWorkingHours[]): ResolvedWorkingHours {
    const enabledSchedules = schedules.filter(s => s.enabled)

    if (enabledSchedules.length === 0) {
        return { ...DEFAULT_WORKING_HOURS, source: 'default' }
    }

    if (enabledSchedules.length === 1) {
        return enabledSchedules[0]
    }

    let earliestStart = enabledSchedules[0].startTime
    let latestEnd = enabledSchedules[0].endTime
    const allDays = new Set<number>()

    for (const schedule of enabledSchedules) {
        if (compareTime(schedule.startTime, earliestStart) < 0) {
            earliestStart = schedule.startTime
        }
        if (compareTime(schedule.endTime, latestEnd) > 0) {
            latestEnd = schedule.endTime
        }
        for (const day of schedule.workingDays) {
            allDays.add(day)
        }
    }

    return {
        enabled: true,
        startTime: earliestStart,
        endTime: latestEnd,
        workingDays: Array.from(allDays).sort((a, b) => a - b),
        source: 'merged',
    }
}
